import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import QuestionView from "../../Question/View/QuestionView";
import { handleDownloadResult } from "../../../Home/Chatbot/services/chatbotDownloadHelper";
import api from "../../../../api/api";
import {
    cleanBotHtml,
    calculateBreakdown,
    getPrecedingPrompt
} from "../utils/deepseekUtils";

const DeepSeekTurnDetailsModal = ({
    show,
    onHide,
    turn,
    activeConvTurns,
    t
}) => {
    const [modalActiveTab, setModalActiveTab] = useState("info");
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedQuestionHistoryIndex, setSelectedQuestionHistoryIndex] = useState(-1);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [resolvedQuestions, setResolvedQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Reset when active turn changes
    useEffect(() => {
        if (show) {
            setModalActiveTab("info");
            setCurrentQuestionIndex(0);
            setSelectedQuestionHistoryIndex(-1);
            setZoomLevel(1.0);
            setResolvedQuestions([]);
        }
    }, [show, turn]);

    // Resolve questions using QuestionsLookupsController
    useEffect(() => {
        if (!turn) {
            setResolvedQuestions([]);
            return;
        }

        const rawQuestions = selectedQuestionHistoryIndex === -1
            ? (turn.Questions || [])
            : ((turn.QuestionsHistory && turn.QuestionsHistory[selectedQuestionHistoryIndex]?.questions) 
               || (turn.QuestionsHistory && turn.QuestionsHistory[selectedQuestionHistoryIndex]?.Questions) 
               || []);

        if (rawQuestions.length === 0) {
            setResolvedQuestions([]);
            return;
        }

        setLoadingQuestions(true);
        const payload = rawQuestions.map(q => ({
            isFromDB: q.IsFromDB || q.isFromDB || false,
            questionId: q.QuestionId || q.questionId || (q.isFromDB ? q.id || q.Id : null),
            lessonId: q.LessonId || q.lessonId || "",
            id: q.id || q.Id || "",
            type: q.Type || q.type || "",
            questionText: q.QuestionText || q.questionText || q.Name || q.name || "",
            options: q.Options || q.options || null,
            correctAnswers: q.CorrectAnswers || q.correctAnswers || [],
            categories: q.Categories || q.categories || null,
            answers: q.Answers || q.answers || null,
            correctMapping: q.CorrectMapping || q.correctMapping || null,
            customStyle: q.CustomStyle || q.customStyle || null,
            hint: q.hint || q.Hint || "",
            wordAndAdvice: q.wordAndAdvice || ""
        }));

        api.post('/QuestionsLookups', payload)
            .then(res => {
                setResolvedQuestions(res.data.data || []);
                setLoadingQuestions(false);
            })
            .catch(() => {
                setResolvedQuestions(rawQuestions);
                setLoadingQuestions(false);
            });
    }, [turn, selectedQuestionHistoryIndex]);

    if (!turn) return null;

    const totalTimeMs = (turn.DBTime || 0) + (turn.AITime || 0);
    const totalTimeSec = (totalTimeMs / 1000).toFixed(2);
    const breakdown = calculateBreakdown(turn.Agent, turn.PromptTokens || 0, turn.CompletionTokens || 0);
    const hasQuestions = turn.Questions && turn.Questions.length > 0;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            dialogClassName="deepseek-details-modal"
            style={{ direction: 'rtl' }}
        >
            <Modal.Header closeButton className="text-right">
                <Modal.Title className="h5 font-weight-bold m-0 d-flex align-items-center gap-2">
                    <i className="fal fa-info-circle text-primary ml-2" style={{ color: '#92278f' }}></i>
                    <span>{t("Pages.DeepSeekAdmin.convDetails") || "تفاصيل استعلام الذكاء الاصطناعي"}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-right">
                <div className="more-info-layout-wrapper">
                    {/* Right Panel: Main details (Usage info or questions) */}
                    <div className="more-info-main-panel">
                        {modalActiveTab === "info" ? (
                            <div className="animate__animated animate__fadeIn animate__faster">
                                {/* Profile Hero Section */}
                                <div className="more-info-hero">
                                    <div className="hero-avatar-wrapper">
                                        <i className="fal fa-robot"></i>
                                    </div>
                                    <div className="hero-details-section">
                                        <h4 className="hero-name">{turn.Agent}</h4>
                                        <div className="hero-meta text-muted mt-1">
                                            <span>{t('Pages.DeepSeekAdmin.totalResponseTime', { time: totalTimeSec })}</span>
                                            <span className="mx-2">•</span>
                                            {turn.Price === 0 ? (
                                                <span className="badge bg-soft-info text-info px-2 py-1 font-weight-bold">
                                                    <i className="fal fa-bolt"></i> {t('Pages.DeepSeekAdmin.fromCache') || "من الكاش"}
                                                </span>
                                            ) : (
                                                <span className="text-success font-weight-bold">{t('Pages.DeepSeekAdmin.costLabel') || "التكلفة:"} ${(turn.Price || 0).toFixed(5)}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Response Time Table with Sum */}
                                <div className="deepseek-modal-card mb-4">
                                    <h6 className="deepseek-modal-section-title">
                                        <i className="fal fa-clock ml-2 text-primary" style={{ color: '#92278f' }}></i> {t('Pages.DeepSeekAdmin.responseTimeTitle')}
                                    </h6>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm text-center mb-0" style={{ fontSize: '12px' }}>
                                            <thead>
                                                <tr className="bg-light">
                                                    <th>{t('Pages.DeepSeekAdmin.processStage')}</th>
                                                    <th>{t('Pages.DeepSeekAdmin.timeMs')}</th>
                                                    <th>{t('Pages.DeepSeekAdmin.timeSec')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className="text-right">{t('Pages.DeepSeekAdmin.dbQueryStage')}</td>
                                                    <td>{(turn.DBTime || 0).toFixed(2)} {t('Pages.DeepSeekAdmin.msAbbr') || "ملي ثانية"}</td>
                                                    <td>{((turn.DBTime || 0) / 1000).toFixed(2)} {t('Pages.Chatbot.SecondsAbbr') || "ث"}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-right">{t('Pages.DeepSeekAdmin.aiProcessingStage')}</td>
                                                    <td>{(turn.AITime || 0).toFixed(2)} {t('Pages.DeepSeekAdmin.msAbbr') || "ملي ثانية"}</td>
                                                    <td>{((turn.AITime || 0) / 1000).toFixed(2)} {t('Pages.Chatbot.SecondsAbbr') || "ث"}</td>
                                                </tr>
                                                <tr className="font-weight-bold table-warning">
                                                    <td className="text-right">{t('Pages.DeepSeekAdmin.totalTimeSum')}</td>
                                                    <td>{totalTimeMs.toFixed(2)} {t('Pages.DeepSeekAdmin.msAbbr') || "ملي ثانية"}</td>
                                                    <td>{totalTimeSec} {t('Pages.Chatbot.SecondsAbbr') || "ث"}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Info Cards Grid (Tokens & Billing) */}
                                <div className="more-info-grid-modern mt-3">
                                    <div className="info-detail-card-modern">
                                        <div className="info-detail-icon-modern text-primary" style={{ color: '#92278f' }}>
                                            <i className="fal fa-sign-in-alt"></i>
                                        </div>
                                        <div className="info-detail-content-modern">
                                            <span className="info-detail-label-modern">{t('Pages.DeepSeekAdmin.promptTokens')}</span>
                                            <span className="info-detail-value-modern">{turn.PromptTokens?.toLocaleString() || 0}</span>
                                        </div>
                                    </div>

                                    <div className="info-detail-card-modern">
                                        <div className="info-detail-icon-modern text-success" style={{ color: '#22c55e' }}>
                                            <i className="fal fa-sign-out-alt"></i>
                                        </div>
                                        <div className="info-detail-content-modern">
                                            <span className="info-detail-label-modern">{t('Pages.DeepSeekAdmin.completionTokens')}</span>
                                            <span className="info-detail-value-modern">{turn.CompletionTokens?.toLocaleString() || 0}</span>
                                        </div>
                                    </div>

                                    <div className="info-detail-card-modern">
                                        <div className="info-detail-icon-modern text-info" style={{ color: '#0ea5e9' }}>
                                            <i className="fal fa-calculator"></i>
                                        </div>
                                        <div className="info-detail-content-modern">
                                            <span className="info-detail-label-modern">{t('Pages.DeepSeekAdmin.totalTokens')}</span>
                                            <span className="info-detail-value-modern">{((turn.PromptTokens || 0) + (turn.CompletionTokens || 0)).toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div className="info-detail-card-modern">
                                         <div className="info-detail-icon-modern text-warning" style={{ color: '#f59e0b' }}>
                                             <i className="fal fa-dollar-sign"></i>
                                         </div>
                                         <div className="info-detail-content-modern">
                                             <span className="info-detail-label-modern">{t('Pages.DeepSeekAdmin.totalCost') || "التكلفة الإجمالية"}</span>
                                             <span className="info-detail-value-modern">
                                                 {turn.Price === 0 ? (
                                                     <span className="badge bg-soft-info text-info px-2 py-1 font-weight-bold" style={{ fontSize: '11px' }}>
                                                         <i className="fal fa-bolt"></i> {t('Pages.DeepSeekAdmin.fromCache') || "من الكاش"}
                                                     </span>
                                                 ) : (
                                                     `$${(turn.Price || 0).toFixed(5)}`
                                                 )}
                                             </span>
                                         </div>
                                     </div>

                                    {turn.SearchTopicToken > 0 && (
                                        <div className="info-detail-card-modern">
                                            <div className="info-detail-icon-modern text-primary" style={{ color: '#a855f7' }}>
                                                <i className="fal fa-tags"></i>
                                            </div>
                                            <div className="info-detail-content-modern">
                                                <span className="info-detail-label-modern">{t('Pages.Chatbot.SearchTopicTokens') || "رموز مطابقة المواضيع"}</span>
                                                <span className="info-detail-value-modern">{turn.SearchTopicToken.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    )}

                                    {turn.SearchTopicPrice > 0 && (
                                        <div className="info-detail-card-modern">
                                            <div className="info-detail-icon-modern text-success" style={{ color: '#10b981' }}>
                                                <i className="fal fa-tags"></i>
                                            </div>
                                            <div className="info-detail-content-modern">
                                                <span className="info-detail-label-modern">{t('Pages.Chatbot.SearchTopicPrice') || "تكلفة مطابقة المواضيع"}</span>
                                                <span className="info-detail-value-modern">${turn.SearchTopicPrice.toFixed(5)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Billing Math Details */}
                                <div className="deepseek-modal-card mt-3">
                                    <h6 className="deepseek-modal-section-title">
                                        <i className="fal fa-calculator ml-2 text-primary" style={{ color: '#92278f' }}></i> {t('Pages.DeepSeekAdmin.pricingDetailsTitle')}
                                    </h6>
                                    <div className="row text-muted small">
                                        <div className="col-md-6 mb-2 text-right">{t('Pages.DeepSeekAdmin.promptPricingLabel', { count: turn.PromptTokens?.toLocaleString() })} (${breakdown.promptCost.toFixed(6)})</div>
                                        <div className="col-md-6 mb-2 text-right">{t('Pages.DeepSeekAdmin.completionPricingLabel', { count: turn.CompletionTokens?.toLocaleString() })} (${breakdown.completionCost.toFixed(6)})</div>
                                        <div className="col-12 mt-1 pt-2 border-top font-weight-bold text-dark text-right" style={{ fontSize: '13px' }}>{t('Pages.DeepSeekAdmin.totalPricingLabel', { cost: breakdown.totalCost.toFixed(6) })}</div>
                                    </div>
                                </div>
                            </div>
                        ) : modalActiveTab === "prompt" ? (
                            <div className="animate__animated animate__fadeIn animate__faster">
                                <h6 className="deepseek-modal-section-title">
                                    <i className="fal fa-file-alt ml-2 text-primary" style={{ color: '#92278f' }}></i> {t('Pages.DeepSeekAdmin.sentPromptLabel')}
                                </h6>
                                <pre 
                                    className="p-3 bg-light border rounded text-right" 
                                    style={{ 
                                        whiteSpace: 'pre-wrap', 
                                        fontFamily: 'Courier New, monospace', 
                                        fontSize: '11px', 
                                        direction: 'rtl',
                                        maxHeight: '400px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    {turn.SendedPrompt || turn.Prompt}
                                </pre>
                            </div>
                        ) : modalActiveTab === "response" ? (
                            <div className="animate__animated animate__fadeIn animate__faster d-flex flex-column h-100">
                                <div className="d-flex align-items-center justify-content-between pb-2 border-bottom mb-3">
                                    <h6 className="deepseek-modal-section-title m-0">
                                        <i className="fal fa-robot ml-2 text-primary" style={{ color: '#92278f' }}></i> {t('Pages.DeepSeekAdmin.aiResponse')}
                                    </h6>
                                    <div className="zoom-controls d-flex align-items-center gap-2">
                                        <button 
                                            type="button"
                                            className="btn btn-outline-secondary btn-xs rounded-circle"
                                            onClick={() => setZoomLevel(prev => Math.max(0.7, prev - 0.1))}
                                            style={{ width: '24px', height: '24px', padding: 0, fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            -
                                        </button>
                                        <span className="small text-muted font-weight-bold" style={{ minWidth: '40px', textAlign: 'center' }}>{Math.round(zoomLevel * 100)}%</span>
                                        <button 
                                            type="button"
                                            className="btn btn-outline-secondary btn-xs rounded-circle"
                                            onClick={() => setZoomLevel(prev => Math.min(1.5, prev + 0.1))}
                                            style={{ width: '24px', height: '24px', padding: 0, fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                <div 
                                    className="p-3 border rounded text-right bg-white"
                                    style={{ 
                                        maxHeight: '450px',
                                        overflowY: 'auto',
                                        fontSize: `${13 * zoomLevel}px`,
                                        lineHeight: '1.6'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: cleanBotHtml(turn.Answer) }}
                                />
                            </div>
                        ) : (
                            <div className="animate__animated animate__fadeIn animate__faster d-flex flex-column h-100">
                                <div className="d-flex align-items-center justify-content-between pb-2 border-bottom mb-3">
                                    <h6 className="deepseek-modal-section-title m-0">
                                        <i className="fal fa-book-open ml-2 text-primary" style={{ color: '#92278f' }}></i> {t('Pages.DeepSeekAdmin.exercisesTitle')}
                                    </h6>
                                    {turn.QuestionsHistory && turn.QuestionsHistory.length > 0 && (
                                        <div className="version-selector d-flex align-items-center gap-2">
                                            <label className="m-0 small text-muted" style={{ fontFamily: 'Cairo, sans-serif' }}>
                                                {t('Pages.DeepSeekAdmin.questionVersion')}
                                            </label>
                                            <select
                                                className="form-control form-control-sm rounded-pill px-2"
                                                style={{ width: "auto", minWidth: "150px", height: '28px', fontSize: '11px', fontFamily: 'Cairo, sans-serif' }}
                                                value={selectedQuestionHistoryIndex}
                                                onChange={(e) => {
                                                    setSelectedQuestionHistoryIndex(parseInt(e.target.value));
                                                    setCurrentQuestionIndex(0);
                                                }}
                                            >
                                                <option value={-1}>{t('Pages.DeepSeekAdmin.currentVersion')} ({turn.Questions?.length || 0} {t('Pages.DeepSeekAdmin.questionCount')})</option>
                                                {turn.QuestionsHistory.map((hist, hIdx) => (
                                                    <option key={hIdx} value={hIdx}>
                                                        {t('Pages.DeepSeekAdmin.versionNum', { num: hIdx + 1 })} ({hist.questions?.length || hist.Questions?.length || 0} {t('Pages.DeepSeekAdmin.questionCount')})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {resolvedQuestions.length > 0 ? (
                                    <>
                                        {/* Tracker dots (Only shown if more than 1 question resolved) */}
                                        {resolvedQuestions.length > 1 && (
                                            <div className="questions-progress-tracker d-flex justify-content-center flex-wrap gap-2 mb-3">
                                                {resolvedQuestions.map((_, idx) => {
                                                    const isActive = idx === currentQuestionIndex;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className={`progress-dot ${isActive ? 'active' : ''}`}
                                                            title={`${t('Pages.Chatbot.QuestionNumber') || "سؤال"} ${idx + 1}`}
                                                            onClick={() => setCurrentQuestionIndex(idx)}
                                                            style={{
                                                                width: '28px',
                                                                height: '28px',
                                                                borderRadius: '50%',
                                                                background: isActive ? '#92278f' : '#e2e8f0',
                                                                color: isActive ? '#ffffff' : '#475569',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '11px',
                                                                fontWeight: 'bold',
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                border: '1px solid rgba(14, 44, 83, 0.05)',
                                                                boxShadow: isActive ? '0 0 0 3px rgba(146, 39, 143, 0.2)' : 'none',
                                                                transform: isActive ? 'scale(1.1)' : 'none',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            {idx + 1}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Question card */}
                                        <div className="visual-theater-card card p-3 p-md-4 mb-3 border shadow-sm rounded-lg flex-grow-1 bg-white text-right">
                                            {resolvedQuestions[currentQuestionIndex] && (() => {
                                                const q = resolvedQuestions[currentQuestionIndex];
                                                return (
                                                    <div className="d-flex flex-column h-100 text-right">
                                                        <div className="question-renderer-wrapper flex-grow-1 text-right mb-3">
                                                            <QuestionView
                                                                key={q.Id || q.id || `q_${currentQuestionIndex}`}
                                                                question={q}
                                                                answers={q.Answers || q.answers}
                                                                index={currentQuestionIndex + 1}
                                                                indexType={1}
                                                                examAnswers={[]}
                                                                saveExamAnswer={() => {}}
                                                                onEdit={false}
                                                                solveButton={false}
                                                                FontSize={18}
                                                                questionSeparated={false}
                                                                isExamQuestion={false}
                                                            />
                                                        </div>

                                                        {/* Hint */}
                                                        {(q.hint || q.Hint) && (
                                                            <div className="alert alert-info border-info text-right p-3 rounded shadow-sm mt-3" style={{ borderRight: '4px solid #17a2b8' }}>
                                                                <h6 className="font-weight-bold text-info mb-1 text-right" style={{ fontSize: '12.5px' }}>
                                                                    <i className="fal fa-lightbulb-on ml-2"></i>{t('Pages.Chatbot.HintTitle')}
                                                                </h6>
                                                                <p className="m-0 text-dark small text-right" style={{ lineHeight: '1.5' }}>
                                                                    {q.hint || q.Hint}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Navigation row (Only shown if more than 1 question resolved) */}
                                        {resolvedQuestions.length > 1 && (
                                            <div className="question-navigation-row d-flex align-items-center justify-content-between border-top pt-3 mt-auto">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                                                    disabled={currentQuestionIndex === 0}
                                                    onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                                                    style={{ borderRadius: '20px' }}
                                                >
                                                    <i className="fal fa-arrow-right ml-1"></i>
                                                    <span>{t('Pages.DeepSeekAdmin.prev')}</span>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                                                    disabled={currentQuestionIndex === resolvedQuestions.length - 1}
                                                    onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                                                    style={{ borderRadius: '20px', backgroundColor: '#92278f', borderColor: '#92278f' }}
                                                >
                                                    <span>{t('Pages.DeepSeekAdmin.next')}</span>
                                                    <i className="fal fa-arrow-left mr-1"></i>
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fal fa-exclamation-triangle mb-2 h4 d-block"></i>
                                        <span>{loadingQuestions ? t('Pages.DeepSeekAdmin.loadingQuestions') : t('Pages.DeepSeekAdmin.noQuestions')}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Left Panel: Control and Navigation actions sidebar */}
                    <div className="more-info-sidebar-panel">
                        <div className="sidebar-section-header-modern">
                            <i className="fal fa-sliders-h ml-2 text-primary" style={{ color: '#92278f' }}></i>
                            <span>{t('Pages.DeepSeekAdmin.sidebarTabsHeader')}</span>
                        </div>

                        <div className="sidebar-actions-scroll">
                            <button 
                                type="button" 
                                className={`sidebar-action-btn-modern ${modalActiveTab === "info" ? "active" : ""}`} 
                                onClick={() => setModalActiveTab("info")}
                            >
                                <i className="fal fa-analytics icon ml-2"></i>
                                <span>{t('Pages.DeepSeekAdmin.processingDetails')}</span>
                            </button>

                            <button 
                                type="button" 
                                className={`sidebar-action-btn-modern ${modalActiveTab === "prompt" ? "active" : ""}`} 
                                onClick={() => setModalActiveTab("prompt")}
                            >
                                <i className="fal fa-file-alt icon ml-2"></i>
                                <span>{t('Pages.DeepSeekAdmin.sentPromptLabel')}</span>
                            </button>

                            <button 
                                type="button" 
                                className={`sidebar-action-btn-modern ${modalActiveTab === "response" ? "active" : ""}`} 
                                onClick={() => setModalActiveTab("response")}
                            >
                                <i className="fal fa-robot icon ml-2"></i>
                                <span>{t('Pages.DeepSeekAdmin.aiResponse')}</span>
                            </button>

                            {hasQuestions && (
                                <button 
                                    type="button" 
                                    className={`sidebar-action-btn-modern ${modalActiveTab === "questions" ? "active" : ""}`} 
                                    onClick={() => setModalActiveTab("questions")}
                                >
                                    <i className="fal fa-question-circle icon ml-2"></i>
                                    <span>{t('Pages.DeepSeekAdmin.exercisesTitle')}</span>
                                </button>
                            )}

                            {turn.SearchWords && turn.SearchWords.length > 0 && (
                                <div className="deepseek-modal-card mt-3">
                                    <h6 className="deepseek-modal-section-title mb-2">
                                        <i className="fal fa-tags ml-2 text-primary" style={{ color: '#92278f' }}></i> {t('Pages.DeepSeekAdmin.suggestedTopicsInResponse')}
                                    </h6>
                                    <div className="d-flex flex-wrap gap-1">
                                        {turn.SearchWords.map((word, wIdx) => (
                                            <span 
                                                key={wIdx} 
                                                className="badge badge-light px-2 py-1 border text-dark font-weight-bold"
                                                style={{ fontSize: '11px', fontFamily: 'Cairo, sans-serif' }}
                                            >
                                                {word}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button 
                                type="button" 
                                className="sidebar-action-btn-modern" 
                                onClick={() => handleDownloadResult(cleanBotHtml(turn.Answer), getPrecedingPrompt(turn, activeConvTurns))}
                            >
                                <i className="fal fa-download icon ml-2 text-success" style={{ color: '#22c55e' }}></i>
                                <span>{t('Pages.DeepSeekAdmin.downloadAnswer')}</span>
                            </button>

                            <button 
                                type="button" 
                                className="sidebar-action-btn-modern danger-zone-btn mt-4" 
                                onClick={onHide}
                            >
                                <i className="fal fa-times-circle icon ml-2 text-danger" style={{ color: '#ef4444' }}></i>
                                <span>{t('Pages.DeepSeekAdmin.closeWindow')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default DeepSeekTurnDetailsModal;
